import React, { useCallback, useMemo, useState } from 'react';
import { Button, ButtonStyles, Toggle } from '@silverfort/sf-components';
import { AD_ENTITY_TYPES } from '@silverfort-nodelibs/common/utils/consts';
import ScopeWrapper, { ScopeWrapperState } from './ScopeWrapper';
import { SaveExposuresConfigPayload, StaticExposureCustomConfig } from '../types';
import CustomizableFields from '../../../../sharedComponents/customizableFields/CustomizableFields';
import { ConfigDefinition } from '../../../../sharedComponents/customizableFields/types';
import { SCOPES_OPTIONS } from '../../../../sharedComponents/scope/consts';
import { TAB_OPTIONS } from '../../../../sharedComponents/EntitiesDualSelectList';
import { omitFields } from '../../../../core/utils/utils';

interface ExposureConfigurationContentProps {
    config: StaticExposureCustomConfig;
    onSave: (exposureConfig: SaveExposuresConfigPayload) => void;
    isSaving?: boolean;
}

const StaticExposureConfigurationContent: React.FC<ExposureConfigurationContentProps> = ({
    config: initialConfig,
    onSave,
    isSaving = false
}) => {
    const [config, setConfig] = useState<StaticExposureCustomConfig>(initialConfig);
    const [isValid, setIsValid] = useState<boolean>(true);

    const addEntitiesTabsOptions = useMemo(() => {
        return config.entityType === AD_ENTITY_TYPES.SERVER
            ? [TAB_OPTIONS.ALL, TAB_OPTIONS.SERVERS, TAB_OPTIONS.GROUPS, TAB_OPTIONS.OUS]
            : [TAB_OPTIONS.ALL, TAB_OPTIONS.USERS, TAB_OPTIONS.GROUPS, TAB_OPTIONS.OUS];
    }, [config]);

    const excludedScope: ScopeWrapperState = {
        entities: Array.isArray(config.excludedUsersAndGroups) ? config.excludedUsersAndGroups : [],
        conditions: config.excludedConditions || []
    };

    const setExcludedScope = useCallback((next: ScopeWrapperState) => {
        setConfig((prev) => ({
            ...prev,
            excludedUsersAndGroups: next.entities,
            excludedConditions: next.conditions
        }));
    }, []);

    const setCustomFields = (customizableFields: Record<string, ConfigDefinition>) => {
        setConfig((prev) => ({
            ...prev,
            customizableFields
        }));
    };

    const setIsActive = (isActive: boolean) => {
        setConfig((prev) => ({
            ...prev,
            isActive
        }));
    };

    const saveConfig = useCallback(() => {
        const formattedConfig = omitFields(config, [
            'customizableFields',
            'description',
            'displayName',
            'category'
        ]) as SaveExposuresConfigPayload;
        formattedConfig.customizableFieldsValues = Object.fromEntries(
            Object.entries(config.customizableFields ?? {}).map(([key, field]) => [key, field.currentValue])
        );
        formattedConfig.lastModified = new Date().getTime();
        onSave(formattedConfig);
    }, [config, onSave]);

    return (
        <div className="flex flex-col justify-between gap-6 min-h-0 w-full h-full">
            <div className="flex flex-col gap-4 overflow-y-auto flex-grow">
                <div className="flex items-center gap-2 px-6 mt-6">
                    <span className="text-sm font-medium">Exposure status</span>
                    <Toggle checked={config.isActive} onChange={setIsActive} className="mt-1" />
                </div>

                <div className="flex gap-6 min-h-[450px] h-full w-full pl-6 pr-6">
                    {config.customizableFields && Object.keys(config.customizableFields).length > 0 && (
                        <div className="min-w-[300px]">
                            <CustomizableFields
                                customizableFields={config.customizableFields}
                                setCustomizableFields={setCustomFields}
                                setIsFormValid={setIsValid}></CustomizableFields>
                        </div>
                    )}

                    <div className="border border-gray-200 rounded-lg flex flex-col justify-between h-full flex-1 divide-gray-200 divide-y min-w-[390px] w-full">
                        <div className="flex flex-col gap-2 p-6 h-full">
                            <div>
                                <h3 className="text-sm font-medium sf-gray-800 mb-1">Exclude</h3>
                                <p className="text-13 sf-gray-800 text-nowrap mb-2">
                                    Exclude entities from detection for this exposure
                                </p>
                            </div>
                            <div className="flex h-full">
                                <ScopeWrapper
                                    scope={SCOPES_OPTIONS.OUT_SCOPE}
                                    state={excludedScope}
                                    onChange={setExcludedScope}
                                    emptyStateTitle="No assets added"
                                    emptyStateDescription="Add assets and select specific services"
                                    addEntityTitle="Add Entity"
                                    tabOptions={addEntitiesTabsOptions}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className="flex justify-end border-t border-gray-200 px-6 py-4 shrink-0 bg-sf-white z-50">
                <Button
                    text={isSaving ? 'Saving...' : 'Apply'}
                    buttonStyle={ButtonStyles.PrimarySolid}
                    disabled={isSaving || !isValid}
                    onClick={saveConfig}
                />
            </div>
        </div>
    );
};

export default StaticExposureConfigurationContent;
